"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, MapPin, Users, MessageSquare, User, Mail, Phone, Info, CircleCheck, Loader, Ruler, Zap, Clock } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RichiediUnPreventivo() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="richiedi-un-preventivo" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--color-primary)_0%,_transparent_30%)] opacity-10 pointer-events-none" />
      
      <div ref={ref} className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {}
        <div className="lg:col-span-7 xl:col-span-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-light tracking-tight mb-4 text-primary">
              Request a Quote
            </h2>
            <p className="text-muted-foreground text-lg font-light">Tell us about your event. Together, we will craft a bespoke analog photography experience tailored just for you.</p>
          </div>

          <FormProvider formId="quote_form_weddings" sectionName="richiedi-un-preventivo" className="space-y-8" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          wedding_date: "",
          wedding_location: "",
          guest_count: "",
          additional_requests: ""
        }} validationRules={{
          full_name: {
            required: "Il nome è obbligatorio"
          },
          email: {
            required: "L'email è obbligatoria",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Inserisci un indirizzo email valido"
            }
          },
          phone: {
            required: "Il telefono è obbligatorio"
          },
          wedding_date: {
            required: "La data è obbligatoria"
          },
          wedding_location: {
            required: "Il luogo è obbligatorio"
          },
          guest_count: {
            min: {
              value: 1,
              message: "Almeno 1 ospite"
            }
          }
        }} submitText="Invia Richiesta" successMessage="Grazie! Abbiamo ricevuto la tua richiesta. Ti risponderemo al più presto con un preventivo personalizzato." errorMessage="Si è verificato un errore durante l'invio. Per favore riprova o contattaci direttamente.">
            <QuoteForm />
          </FormProvider>
        </div>

        {}
        <aside className="lg:col-span-5 xl:col-span-4 space-y-12 pt-8 lg:pt-0">
          <div className="bg-card/30 border border-border/40 p-8 backdrop-blur-sm">
            <h3 className="text-xl font-serif mb-6 flex items-center gap-3 text-primary"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-info size-5" aria-hidden="true" data-ast-id="f:/src/pages/Weddings/sections/RichiediUnPreventivo.tsx|sig:cvdw2q" data-ast-start="3276" data-ast-end="3303"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>Planning Guide</h3>
            
            <div className="space-y-8">
              <div className="flex gap-4 group">
                <div className="mt-1 bg-primary/10 p-2 rounded-full h-fit group-hover:bg-primary/20 transition-colors">
                  <Ruler className="size-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium mb-1 text-foreground">Space Required</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">Our photo carts and bikes require a flat area of approximately 2.5x2.5 meters. Ideal for both indoor and outdoor settings (squares, gardens).</p>
                </div>
              </div>

              <div className="flex gap-4 group">
                <div className="mt-1 bg-primary/10 p-2 rounded-full h-fit group-hover:bg-primary/20 transition-colors">
                  <Zap className="size-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium mb-1 text-foreground">No Power Required</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">Our machines are entirely analog and mechanical. However, for evening events, we may require a power source to ensure the best possible lighting and performance.</p>
                </div>
              </div>

              <div className="flex gap-4 group">
                <div className="mt-1 bg-primary/10 p-2 rounded-full h-fit group-hover:bg-primary/20 transition-colors">
                  <Clock className="size-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium mb-1 text-foreground">Timing</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">Each portrait takes approximately 3-7 minutes for the shot and immediate development, engaging guests in the magical process as the image appears before their eyes.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-l-2 border-primary/30 pl-6">
            <p className="italic text-muted-foreground font-serif text-lg">"A tangible memory in a digital world. We give your guests a piece of history."</p>
          </div>
        </aside>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/5 border border-primary/20 text-center animate-in fade-in zoom-in duration-500">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-primary/10 mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif mb-3 text-foreground">Request Submitted</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">Thanks! Your request has been received. We’ll be in touch shortly with a custom quote.</p>
        <Button onClick={resetForm} variant="outline" className="border-primary/30 hover:bg-primary/10 hover:text-primary">Submit a new request</Button>
      </div>;
  }
  const ledgerInputClass = "border-0 border-b border-input rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary transition-colors h-12 text-lg placeholder:text-muted-foreground/50";
  const ledgerLabelClass = "text-xs uppercase tracking-widest text-muted-foreground font-medium mb-0";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        {}
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={ledgerLabelClass}>Full Name *</FormLabel>
              <FormControl>
                <div className="relative flex items-center">
                  <User className="absolute left-0 size-4 text-muted-foreground/30" />
                  <Input placeholder="Mario Rossi" {...field} className={`${ledgerInputClass} pl-8`} />
                </div>
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/40">Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={ledgerLabelClass}>Email *</FormLabel>
              <FormControl>
                <div className="relative flex items-center">
                  <Mail className="absolute left-0 size-4 text-muted-foreground/30" />
                  <Input type="email" placeholder="mario@example.com" {...field} className={`${ledgerInputClass} pl-8`} />
                </div>
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/40">We’ll email you the quote.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={ledgerLabelClass}>phone *</FormLabel>
              <FormControl>
                <div className="relative flex items-center">
                  <Phone className="absolute left-0 size-4 text-muted-foreground/30" />
                  <Input type="tel" placeholder="+39 333 1234567" {...field} className={`${ledgerInputClass} pl-8`} />
                </div>
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/40">For any clarifications by phone.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="wedding_date" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={ledgerLabelClass}>Wedding Date *</FormLabel>
              <FormControl>
                <div className="relative flex items-center">
                  <SafeHydrate><Calendar className="absolute left-0 size-4 text-muted-foreground/30" /></SafeHydrate>
                  <Input type="date" {...field} className={`${ledgerInputClass} pl-8`} />
                </div>
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/40">If possible, please provide the exact date.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="wedding_location" render={({
        field
      }) => <FormItem className="space-y-1 md:col-span-2">
              <FormLabel className={ledgerLabelClass}>Wedding Venue *</FormLabel>
              <FormControl>
                <div className="relative flex items-center">
                  <MapPin className="absolute left-0 size-4 text-muted-foreground/30" />
                  <Input placeholder="Villa del Lago, Como" {...field} className={`${ledgerInputClass} pl-8`} />
                </div>
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/40">City, region, or specific location.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="guest_count" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={ledgerLabelClass}>Approx. Number of Guests</FormLabel>
              <FormControl>
                <div className="relative flex items-center">
                  <Users className="absolute left-0 size-4 text-muted-foreground/30" />
                  <Input type="number" placeholder="150" {...field} className={`${ledgerInputClass} pl-8`} />
                </div>
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/40">This helps us tailor the service to your needs..</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="additional_requests" render={({
        field
      }) => <FormItem className="space-y-1 md:col-span-2">
              <FormLabel className={ledgerLabelClass}>Special Requests / Notes</FormLabel>
              <FormControl>
                <div className="relative">
                  <MessageSquare className="absolute left-0 top-4 size-4 text-muted-foreground/30" />
                  <textarea placeholder="For example: timing, special themes, etc." {...field} className={`flex w-full min-h-[100px] py-3 text-base md:text-sm placeholder:text-muted-foreground/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${ledgerInputClass} pl-8 resize-y`} />
                </div>
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/40">Any additional information you find helpful.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-medium tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 hover:w-[102%] transition-all duration-300 rounded-none shadow-lg shadow-primary/10">Get a Quote</Button>
      </div>
    </Form>;
}